/**
 * customização: abas You'll Also Love / Recently Viewed + progresso do carrossel (scroll nativo).
 */
const RECENTLY_VIEWED_KEY = 'recently_viewed_products';
const RECENTLY_VIEWED_LIMIT = 12;

window.DawnRecentlyViewed = window.DawnRecentlyViewed || {
  get() {
    try {
      const raw = JSON.parse(localStorage.getItem(RECENTLY_VIEWED_KEY) || '[]');
      return Array.isArray(raw) ? raw : [];
    } catch (e) {
      return [];
    }
  },

  set(items) {
    try {
      localStorage.setItem(RECENTLY_VIEWED_KEY, JSON.stringify(items.slice(0, RECENTLY_VIEWED_LIMIT)));
    } catch (e) {
      // modo privado / storage cheio — degrada sem erro
    }
  },

  track(productId, handle) {
    if (!productId || !handle) return;
    const id = String(productId);
    const next = this.get().filter((item) => String(item.id) !== id);
    next.unshift({ id, handle });
    this.set(next);
  },
};

class ProductRecommendationsTabs extends HTMLElement {
  connectedCallback() {
    if (this._initialized) return;
    this._initialized = true;

    this.productId = this.dataset.productId;
    this.productHandle = this.dataset.productHandle;
    this.tabs = Array.from(this.querySelectorAll('[data-tab]'));
    this.panels = Array.from(this.querySelectorAll('[data-panel]'));
    this.progressWrap = this.querySelector('[data-carousel-progress]');
    this.progressFill = this.querySelector('[data-carousel-progress-fill]');
    this.statusEl = this.querySelector('[data-recommendations-status]');
    this.recentTrack = this.querySelector('[data-recent-track]');
    this.activeTrack = null;

    // registrar visita atual
    window.DawnRecentlyViewed.track(this.productId, this.productHandle);

    this.tabs.forEach((tab) => {
      tab.addEventListener('click', () => this.activateTab(tab.dataset.tab));
    });

    this.bindTracks();
    this.activateTab(this.dataset.defaultTab || this.tabs[0]?.dataset.tab);
    this.loadRecentlyViewed();
  }

  disconnectedCallback() {
    this.activeTrack?.removeEventListener('scroll', this._onTrackScroll);
  }

  activateTab(name) {
    if (!name) return;

    this.tabs.forEach((tab) => {
      const active = tab.dataset.tab === name;
      tab.classList.toggle('is-active', active);
      tab.setAttribute('aria-selected', active ? 'true' : 'false');
    });

    this.panels.forEach((panel) => {
      const active = panel.dataset.panel === name;
      panel.classList.toggle('hidden', !active);
      panel.toggleAttribute('hidden', !active);
    });

    const activePanel = this.panels.find((panel) => panel.dataset.panel === name);
    const track = activePanel?.querySelector('[data-carousel-track]');
    this.setActiveTrack(track);
  }

  bindTracks() {
    this.querySelectorAll('[data-carousel-track]').forEach((track) => {
      track.addEventListener(
        'scroll',
        () => {
          if (track === this.activeTrack) this.updateProgress(track);
        },
        { passive: true }
      );
    });
  }

  setActiveTrack(track) {
    this.activeTrack = track || null;
    this.updateProgress(this.activeTrack);
  }

  updateProgress(track) {
    if (!this.progressFill) return;
    if (!track) {
      this.progressFill.style.width = '0%';
      return;
    }

    const max = track.scrollWidth - track.clientWidth;
    const pct = max > 0 ? (track.scrollLeft / max) * 100 : 0;
    this.progressFill.style.width = `${Math.max(0, Math.min(100, pct))}%`;
  }

  async loadRecentlyViewed() {
    if (!this.recentTrack) return;

    const items = window.DawnRecentlyViewed.get().filter(
      (item) => String(item.id) !== String(this.productId) && item.handle
    );

    if (!items.length) {
      this.recentTrack.setAttribute('aria-busy', 'false');
      this.recentTrack.innerHTML = `<p class="product-recommendations-tabs__empty">No recently viewed products.</p>`;
      this.updateProgress(this.activeTrack);
      return;
    }

    const limit = Number(this.dataset.skeletonCount || 8);
    const targets = items.slice(0, limit);

    // mantém skeletons até resolver
    try {
      const products = await Promise.all(
        targets.map(async (item) => {
          const response = await fetch(`/products/${item.handle}.js`);
          if (!response.ok) return null;
          return response.json();
        })
      );

      const valid = products.filter(Boolean);
      if (!valid.length) {
        this.recentTrack.innerHTML = `<p class="product-recommendations-tabs__empty">No recently viewed products.</p>`;
      } else {
        const cardSettings = {
          showSecondImage: false,
          showWishlist: false,
          showQuickAdd: this.dataset.showQuickAdd !== 'false',
          showSizeBar: this.dataset.showSizeBar !== 'false',
          showColorDots: this.dataset.showColorDots !== 'false',
          colorDotsPosition: this.dataset.colorDotsPosition || 'inline_with_bag',
          progressBarHeight: Number(this.dataset.progressBarHeight || 4),
          aspectRatio: this.dataset.aspectRatio || '0.7',
          showDiscountBadge: this.dataset.showDiscountBadge !== 'false',
          discountBadgePosition: this.dataset.discountBadgePosition || 'bottom_right',
        };

        this.recentTrack.innerHTML = valid
          .map(
            (product) => `
              <div class="product-recommendations-tabs__slide" role="listitem">
                ${window.ProductCard.buildHTML(product, {
                  moneyFormat: this.dataset.moneyFormat,
                  colorOptionName: this.dataset.colorOptionName,
                  sizeOptionName: this.dataset.sizeOptionName,
                  salePriceColor: this.dataset.salePriceColor || '',
                  settings: cardSettings,
                  strings: {
                    selectSize: this.dataset.selectSizeLabel,
                    addToWishlist: this.dataset.addWishlistLabel,
                    quickAdd: this.dataset.quickAddLabel,
                    discountPercent: this.dataset.discountLabel || '-{{ percent }}%',
                  },
                })}
              </div>
            `
          )
          .join('');
      }
    } catch (error) {
      console.error(error);
      this.recentTrack.innerHTML = `<p class="product-recommendations-tabs__empty">No recently viewed products.</p>`;
    }

    this.recentTrack.setAttribute('aria-busy', 'false');
    if (this.activeTrack === this.recentTrack || this.querySelector('[data-panel="recent"]:not([hidden])')) {
      this.setActiveTrack(this.recentTrack);
    }
  }
}

if (!customElements.get('product-recommendations-tabs')) {
  customElements.define('product-recommendations-tabs', ProductRecommendationsTabs);
}
